/**
 * The single entry point UI code uses for data: `import { api } from '../api'`.
 *
 * With `VITE_USE_MOCKS !== 'false'` (the default) calls go to the in-browser mock
 * layer; otherwise to the real `/admin/v1` and `/ops/v1` endpoints. The mock module
 * is dynamically imported so it is split into its own chunk and never fetched
 * when mocks are off.
 */

import { httpApi } from './client';
import type { RegistrarApi } from './types';

let implementation: Promise<RegistrarApi> | undefined;

function resolveApi(): Promise<RegistrarApi> {
  // Same test as USE_MOCKS in config/env.ts, written inline on purpose: Vite replaces
  // `import.meta.env.*` with a literal here, so a VITE_USE_MOCKS=false build constant-folds
  // this branch away and does not emit the mock chunk at all.
  implementation ??=
    import.meta.env.VITE_USE_MOCKS !== 'false' ? import('./mock').then((m) => m.mockApi) : Promise.resolve(httpApi);
  return implementation;
}

type AnyMethod = (...args: unknown[]) => Promise<unknown>;

/** Every method forwards to the resolved implementation (the method list is the `RegistrarApi` type). */
export const api: RegistrarApi = new Proxy({} as RegistrarApi, {
  get: (_target, key) => {
    // Never look thenable, or `await api` would try to call `api.then`.
    if (typeof key !== 'string' || key === 'then') return undefined;
    return (...args: unknown[]) => resolveApi().then((impl) => (impl[key as keyof RegistrarApi] as AnyMethod)(...args));
  },
});

export { ApiError, isApiError, isAbortError, API_BASE, OPS_BASE } from './client';
export { DISCLOSURE_FIELDS, DISPUTE_APPROVAL_RESOURCE, LEGAL_UNLOCK_ACTION } from './types';
export type * from './types';
